import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Progress } from './ui/progress';
import { ChevronRight, ChevronLeft, Download, RotateCcw, Thermometer, Home, Building2, Sun, Users } from 'lucide-react';
import Step1RoomBasics from './calculator-steps/Step1RoomBasics';
import Step2BuildingCharacteristics from './calculator-steps/Step2BuildingCharacteristics';
import Step3WindowsSun from './calculator-steps/Step3WindowsSun';
import Step4OccupancyHeat from './calculator-steps/Step4OccupancyHeat';
import Step5Climate from './calculator-steps/Step5Climate';
import ResultsDisplay from './calculator-steps/ResultsDisplay';
import { calculateBTU } from '../utils/btuCalculations';
import { generatePDF } from '../utils/pdfGenerator';

const BTUCalculator = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showResults, setShowResults] = useState(false);
  const [unitSystem, setUnitSystem] = useState('metric'); // metric or imperial
  const [advancedMode, setAdvancedMode] = useState(true);
  
  const [formData, setFormData] = useState({
    // Step 1: Room Basics
    roomLength: '',
    roomWidth: '',
    roomHeight: '2.7',
    roomType: 'living-room',
    
    // Step 2: Building Characteristics
    insulationQuality: 'average',
    externalWalls: 2,
    buildingAge: '5-15',
    floorLocation: 'upper',
    
    // Step 3: Windows & Sun
    numberOfWindows: 2,
    windowType: 'double',
    windowOrientation: 'south',
    sunExposure: 'partial',
    windowShading: 'blinds',
    
    // Step 4: Occupancy & Heat
    numberOfOccupants: 2,
    activityLevel: 'moderate',
    heatAppliances: 3,
    lightingType: 'led',
    hasKitchen: false,
    
    // Step 5: Climate
    outsideTemp: 35,
    insideTemp: 22,
    humidityLevel: 'standard'
  });
  
  const [calculationResults, setCalculationResults] = useState(null);
  
  const totalSteps = 5;
  const progress = (currentStep / totalSteps) * 100;
  
  const stepIcons = {
    1: Home,
    2: Building2,
    3: Sun,
    4: Users,
    5: Thermometer
  };
  
  const stepTitles = {
    1: 'Room Basics',
    2: 'Building Details',
    3: 'Windows & Sun',
    4: 'Occupancy & Heat',
    5: 'Climate Settings'
  };
  
  // Real-time calculation whenever form data changes
  useEffect(() => {
    if (formData.roomLength && formData.roomWidth && formData.roomHeight) {
      const results = calculateBTU(formData);
      setCalculationResults(results);
    }
  }, [formData]);
  
  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const isStepValid = () => {
    switch(currentStep) {
      case 1:
        return formData.roomLength && formData.roomWidth && formData.roomHeight;
      case 2:
      case 3:
      case 4:
      case 5:
        return true;
      default:
        return false;
    }
  };
  
  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    } else {
      setShowResults(true);
    }
  };
  
  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };
  
  const handleReset = () => {
    setCurrentStep(1);
    setShowResults(false);
    setFormData({
      roomLength: '',
      roomWidth: '',
      roomHeight: '2.7',
      roomType: 'living-room',
      insulationQuality: 'average',
      externalWalls: 2,
      buildingAge: '5-15',
      floorLocation: 'upper',
      numberOfWindows: 2,
      windowType: 'double',
      windowOrientation: 'south',
      sunExposure: 'partial',
      windowShading: 'blinds',
      numberOfOccupants: 2,
      activityLevel: 'moderate',
      heatAppliances: 3,
      lightingType: 'led',
      hasKitchen: false,
      outsideTemp: 35,
      insideTemp: 22,
      humidityLevel: 'standard'
    });
  };
  
  const handleDownloadPDF = () => {
    if (calculationResults) {
      generatePDF(formData, calculationResults);
    }
  };
  
  const renderStep = () => {
    const stepProps = { formData, updateFormData, unitSystem };
    
    switch(currentStep) {
      case 1:
        return <Step1RoomBasics {...stepProps} />;
      case 2:
        return <Step2BuildingCharacteristics {...stepProps} />;
      case 3:
        return <Step3WindowsSun {...stepProps} />;
      case 4:
        return <Step4OccupancyHeat {...stepProps} />;
      case 5:
        return <Step5Climate {...stepProps} />;
      default:
        return null;
    }
  };
  
  if (showResults) {
    return (
      <ResultsDisplay 
        formData={formData}
        results={calculationResults}
        onStartOver={handleReset}
        onDownloadPDF={handleDownloadPDF}
      />
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-blue-100 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_hvac-calculator/artifacts/6t9eyga4_Algarve-Seasons%20logo.jpg" 
                alt="Algarve Seasons" 
                className="h-12 w-auto"
              />
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleReset}
              className="text-blue-700 border-blue-200 hover:bg-blue-50"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset
            </Button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Progress Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">BTU Calculator</h1>
              <p className="text-gray-600 mt-1">Professional Air Conditioning Sizing</p>
            </div>
            <div className="text-right">
              <div className="text-sm font-medium text-gray-600">Step {currentStep} of {totalSteps}</div>
              <div className="text-xs text-gray-500">{stepTitles[currentStep]}</div>
            </div>
          </div>
          
          <Progress value={progress} className="h-2" />
          
          {/* Step Indicators */}
          <div className="flex justify-between mt-4">
            {[1, 2, 3, 4, 5].map((step) => {
              const StepIcon = stepIcons[step];
              const isCompleted = step < currentStep;
              const isCurrent = step === currentStep;
              
              return (
                <div 
                  key={step}
                  className={`flex flex-col items-center ${
                    isCompleted ? 'text-blue-700' : 
                    isCurrent ? 'text-blue-700' : 
                    'text-gray-400'
                  } transition-colors duration-300`}
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all duration-300 ${
                    isCompleted ? 'bg-blue-700 shadow-lg scale-105' : 
                    isCurrent ? 'bg-blue-700 shadow-lg scale-110' : 
                    'bg-gray-200'
                  }`}>
                    <StepIcon className={`w-6 h-6 ${
                      isCompleted || isCurrent ? 'text-white' : 'text-gray-500'
                    }`} />
                  </div>
                  <span className="text-xs font-medium text-center hidden sm:block">
                    {stepTitles[step]}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Step Content */}
        <Card className="p-6 sm:p-8 shadow-lg border-blue-100">
          <div className="min-h-[400px]">
            {renderStep()}
          </div>
          
          {/* Navigation Buttons */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className="border-blue-200 text-blue-700 hover:bg-blue-50 disabled:opacity-50"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>
            
            <div className="text-sm text-gray-600">
              {currentStep}/{totalSteps} steps completed
            </div>
            
            <Button
              onClick={handleNext}
              disabled={!isStepValid()}
              className="bg-blue-700 hover:bg-blue-800 text-white disabled:opacity-50 shadow-md hover:shadow-lg transition-all duration-300"
            >
              {currentStep === totalSteps ? 'View Results' : 'Next'}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </Card>
        
        {/* Live Preview Card */}
        {calculationResults && (
          <Card className="mt-6 p-6 bg-gradient-to-r from-blue-700 to-blue-800 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm mb-1">Estimated BTU Requirement</p>
                <p className="text-4xl font-bold">{calculationResults.totalBTU.toLocaleString()}</p>
                <p className="text-blue-100 text-sm mt-1">
                  {calculationResults.totalKW.toFixed(2)} kW
                </p>
              </div>
              <div className="text-right">
                <p className="text-blue-100 text-sm mb-1">Room Size</p>
                <p className="text-2xl font-bold">{calculationResults.roomArea.toFixed(1)} m²</p>
                <p className="text-blue-100 text-sm mt-1">
                  {calculationResults.roomVolume.toFixed(1)} m³
                </p>
              </div>
            </div>
          </Card>
        )}
      </main>
      
    </div>
  );
};

export default BTUCalculator;